import { toast } from "react-toastify";
import { ApiFund } from "../funds/ApiFund";
import { ShowToast } from "./ShowToast";

// Shared minimalist layout and typography styles
const baseStyles = `
  * { box-sizing: border-box; }
  body {
    margin: 0;
    height: 100vh;
    display: flex;
    justify-content: center;
    align-items: center;
    background: #f8fafc;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    -webkit-font-smoothing: antialiased;
  }
  .container {
    background: #ffffff;
    width: 400px;
    max-width: 90%;
    padding: 48px 36px;
    border-radius: 24px;
    text-align: center;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 20px 25px -5px rgba(0, 0, 0, 0.05);
    animation: scaleUp 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
  }
  h2 { margin: 0 0 10px; color: #0f172a; font-size: 24px; font-weight: 600; letter-spacing: -0.02em; }
  p { margin: 0 0 28px; color: #64748b; font-size: 15px; line-height: 1.6; }
  @keyframes scaleUp {
    from { opacity: 0; transform: scale(0.95) translateY(8px); }
    to { opacity: 1; transform: scale(1) translateY(0); }
  }
`;

const writeLoadingPage = (receiptWindow, loadingMessage) => {
  if (!receiptWindow) return;

  try {
    receiptWindow.document.open();
    receiptWindow.document.write(`
      <!DOCTYPE html>
      <html lang="en">
      <head>
        <title>${loadingMessage}</title>
        <meta charset="UTF-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <style>
          ${baseStyles}
          
          .spinner-box {
            position: relative;
            width: 56px;
            height: 56px;
            margin: 0 auto 28px;
          }
          
          .spinner {
            width: 100%;
            height: 100%;
            animation: rotate 0.8s linear infinite;
            color: #0f172a;
          }
          
          @keyframes rotate {
            to { transform: rotate(360deg); }
          }

          .status {
            display: inline-flex;
            align-items: center;
            gap: 8px;
            color: #334155;
            font-weight: 500;
            font-size: 14px;
            background: #f1f5f9;
            padding: 10px 20px;
            border-radius: 9999px;
            animation: pulse 2s infinite ease-in-out;
          }

          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.6; }
          }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="spinner-box">
            <svg class="spinner" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="3" style="opacity: 0.12;"></circle>
              <path fill="currentColor" d="M12 2a10 10 0 0 0-10 10h3a7 7 0 0 1 7-7V2z"></path>
            </svg>
          </div>

          <h2>Generating Receipt</h2>
          <p>Please wait while we generate your receipt...</p>
          
          <div id="status" class="status">
            Connecting...
          </div>
        </div>
      </body>
      </html>
    `);
    receiptWindow.document.close();
  } catch {
    // Ignore cross-browser document access issues
  }
};

const updateStatus = (receiptWindow, status) => {
  try {
    if (receiptWindow && !receiptWindow.closed) {
      const statusElement = receiptWindow.document.getElementById("status");
      if (statusElement) {
        statusElement.innerText = status;
      }
    }
  } catch {
    // Ignore
  }
};

const writeErrorPage = (receiptWindow) => {
  try {
    receiptWindow.document.open();
    receiptWindow.document.write(`
      <!DOCTYPE html>
      <html lang="en">
      <head>
        <title>Receipt Error</title>
        <meta charset="UTF-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <style>
          ${baseStyles}

          .error-badge {
            width: 56px;
            height: 56px;
            background: #fef2f2;
            color: #ef4444;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            margin: 0 auto 24px;
          }

          button {
            width: 100%;
            padding: 14px;
            border: 1px solid #e2e8f0;
            background: #ffffff;
            color: #0f172a;
            font-size: 15px;
            font-weight: 500;
            border-radius: 14px;
            cursor: pointer;
            transition: all 0.15s ease;
          }

          button:hover {
            background: #f8fafc;
            border-color: #cbd5e1;
          }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="error-badge">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </div>
          
          <h2>Generation Failed</h2>
          <p>We couldn't load your receipt. The session might have expired or the network interrupted.</p>

          <button onclick="window.close()">
            Close Window
          </button>
        </div>
      </body>
      </html>
    `);
    receiptWindow.document.close();
  } catch {
    // Ignore
  }
};



export const viewReceiptPdf = ({
  transactionNo,
  type,
  t,
  onSuccess,
  onError,
}) => {
  const loadingMessage = t?.("receipt_loading") || "Loading Receipt...";
  const toastId = toast.loading(loadingMessage);
  const receiptWindow = window.open("", "_blank");

  if (!receiptWindow) {
    const err = new Error("Receipt window was blocked.");
    ShowToast(toastId, "error_viewing_receipt", "error");
    if (typeof onError === "function") onError(err);
    return Promise.reject(err);
  }

  writeLoadingPage(receiptWindow, loadingMessage);
  updateStatus(receiptWindow, "Generating PDF...");

  return ApiFund()
    .GenerateReceipt(transactionNo, type)
    .then((res) => {
      updateStatus(receiptWindow, "Opening receipt...");

      const blob = new Blob([res.data], { type: "application/pdf" });
      const blobUrl = URL.createObjectURL(blob);

      if (receiptWindow.closed) {
        URL.revokeObjectURL(blobUrl);
        throw new Error("Receipt window was closed.");
      }

      receiptWindow.location.href = blobUrl;

      setTimeout(() => {
        URL.revokeObjectURL(blobUrl);
      }, 30000);

      ShowToast(toastId, "receipt_opened", "success");
      if (typeof onSuccess === "function") onSuccess();
      return true;
    })
    .catch((err) => {
      console.error("Error viewing receipt:", err);

      if (receiptWindow && !receiptWindow.closed) {
        writeErrorPage(receiptWindow);
      }

      ShowToast(toastId, "error_viewing_receipt", "error");
      if (typeof onError === "function") onError(err);
      throw err;
    });
};

export const viewUsraReceiptPdf = ({
  receiptToken,
  t,
  onSuccess,
  onError,
}) => {
  const loadingMessage = t?.("receipt_loading") || "Loading Receipt...";
  const toastId = toast.loading(loadingMessage);
  const receiptWindow = window.open("", "_blank");

  if (!receiptWindow) {
    const err = new Error("Receipt window was blocked.");
    ShowToast(toastId, "error_viewing_receipt", "error");
    if (typeof onError === "function") onError(err);
    return Promise.reject(err);
  }

  writeLoadingPage(receiptWindow, loadingMessage);
  updateStatus(receiptWindow, "Generating PDF...");

  return ApiFund()
    .GetUsraReceipt(receiptToken)
    .then((res) => {
      updateStatus(receiptWindow, "Opening receipt...");

      const blob = new Blob([res.data], { type: "application/pdf" });
      const blobUrl = URL.createObjectURL(blob);

      if (receiptWindow.closed) {
        URL.revokeObjectURL(blobUrl);
        throw new Error("Receipt window was closed.");
      }

      receiptWindow.location.href = blobUrl;

      setTimeout(() => {
        URL.revokeObjectURL(blobUrl);
      }, 30000);

      ShowToast(toastId, "receipt_opened", "success");
      if (typeof onSuccess === "function") onSuccess();
      return true;
    })
    .catch((err) => {
      console.error("Error viewing usra receipt:", err);

      if (receiptWindow && !receiptWindow.closed) {
        writeErrorPage(receiptWindow);
      }

      ShowToast(toastId, "error_viewing_receipt", "error");
      if (typeof onError === "function") onError(err);
      throw err;
    });
};